import { cloneElement, type FC, isValidElement } from 'react'
import { Link } from 'react-router-dom' // Import React Router Link
import styled from 'styled-components'

import { environment } from '@/config/environment'
import { serializeCMSContentBlock } from './serialize'

import type { ContentBlock } from '../../types/content.type'

type Props = {
  className?: string
  contentBlocks: ContentBlock[]
}

export const RichText: FC<Props> = ({ className, contentBlocks }: Props) => {
  if (!contentBlocks) {
    return null
  }

  return (
    <Content className={className}>
      {contentBlocks
        .filter((c) => c.typeHandle)
        .map((contentBlock, index) => serializeCMSContentBlockWithLinkHandling(contentBlock, index))}
    </Content>
  )
}

/**
 * Custom function to modify links inside the content before rendering.
 */
const serializeCMSContentBlockWithLinkHandling = (contentBlock: ContentBlock, index: number) => {
  // Serialize content block as usual
  const serializedContent = serializeCMSContentBlock(contentBlock, index)

  // Recursively process React elements and modify links
  const processElement = (element: any): any => {
    if (!element) return element

    // If element is an array, process each child
    if (Array.isArray(element)) {
      return element.map((child) => processElement(child))
    }

    // If element is a React element
    if (isValidElement(element)) {
      // Check if it's an <a> tag
      if (element.type === 'a' && (element.props as any).href) {
        const originalUrl = (element.props as any).href
        const urlMatchPattern = new RegExp(environment.craft.urlMatch)

        const match = originalUrl.match(urlMatchPattern)
        if (match) {
          const slug = match[1]
          const newUrl = `/content/article/${slug}`

          // Return a React Router <Link> instead of <a>
          return (
            <Link to={newUrl} {...(element as any).props}>
              {(element as any).props.children}
            </Link>
          )
        }
      }

      // Process children recursively
      return cloneElement(element, (element as any).props, processElement((element as any).props.children))
    }

    return element
  }

  return processElement(serializedContent)
}

const Content = styled.div`
  display: flex;
  flex-direction: column;
  /* Styling for unordered lists */
  ul {
    padding-left: 1.5em;
    margin-bottom: 1em;
    list-style-type: square;
  }

  /* Styling for ordered lists */
  ol {
    padding-left: 1.5em;
    margin-bottom: 1em;
    list-style-type: decimal;
  }

  /* Styling for list items */
  li {
    margin-bottom: 0.5em;
  }

  /* Styling for blockquotes */
  blockquote {
    border-left: 4px solid #ccc;
    padding-left: 1em;
    margin: 1.5em 0;
    font-style: italic;
    color: #555;
  }

  /* Styling for inline code */
  code {
    background-color: #f4f4f4;
    padding: 0.2em 0.4em;
    border-radius: 3px;
    font-family: 'Courier New', Courier, monospace;
  }

  /* Styling for horizontal rule */
  hr {
    border: 0;
    height: 1px;
    background: #ddd;
    margin: 2em 0;
  }

  /* Styling for links */
  a {
    color: #007bff;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  /* Styling for bold text */
  b,
  strong {
    font-weight: bold;
  }

  /* Styling for italic text */
  i,
  em {
    font-style: italic;
  }

  /* Styling for underlined text */
  u {
    text-decoration: underline;
  }

  /* Styling for strikethrough text */
  s,
  strike,
  del {
    text-decoration: line-through;
  }

  /* Styling for subscript */
  sub {
    vertical-align: sub;
    font-size: smaller;
  }

  /* Styling for superscript */
  sup {
    vertical-align: super;
    font-size: smaller;
  }

  h1,
  h2,
  h3,
  h4 {
    margin-top: 1rem;
  }

  p {
    margin-bottom: 1rem;
  }

  figure {
    figcaption {
      display: none;
    }
  }
`
